import { withNamespaces } from 'react-i18next';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { Row, Col, Card, CardBody, Button, Container } from 'reactstrap';
import Navigator from "src/components/Navigator";
import {
	isSettingEnabledForActiveModule
} from 'src/helpers/module_helper';
import reportAIcon from "src/assets/images/report_anonymously.svg";
import reportCIcon from "src/assets/images/report_confidently.svg";
import arrangeMeetingIcon from "src/assets/images/arrange_meeting.svg";
import voiceReportIcon from "src/assets/images/voice.svg";
import { selectOrgCustomization } from 'src/store/orgCustomizations/reducer';

/**
 * Reactstrap Col has no xxl prop — use Bootstrap classes.
 * 4 cards: 2×2 until xxl (1400px); 4-across only on wide desktops.
 * Mid laptop / sidebar widths stay 2 columns so title+icon do not collide.
 */
const getCardColClassName = (enabledCount) => {
    if (enabledCount >= 4) {
        return 'col-12 col-md-6 col-xxl-3';
    }
    if (enabledCount === 3) {
        return 'col-12 col-md-6 col-xl-4';
    }
    if (enabledCount === 2) {
        return 'col-12 col-md-6';
    }
    return 'col-12';
};

const CMHome = ({
    t,
    selectedLanguage,
    Organization,
    orgCustomization
}) => {
    const isAnonymously = isSettingEnabledForActiveModule('isAnonymously');
    const isConfidentially = isSettingEnabledForActiveModule('isConfidentially');
    const isMeeting = isSettingEnabledForActiveModule('isMeeting');
    const isVoice = isSettingEnabledForActiveModule('isVoice');

    const enabledCount = [isAnonymously, isConfidentially, isMeeting, isVoice].filter(Boolean).length;
    const cardColClassName = getCardColClassName(enabledCount);

    const breadCrumbs = [
        { title: t('SIP'), link: '/' },
        { title: t('Home'), link: '#' },
    ];

    return (
        <div className='page-content'>
            <Container fluid>
                {Organization.translations
                    .filter((tr) => tr.module === 'cm')
                    .map((item, index) => (
                        <Row key={index} className={item.lang === selectedLanguage ? '' : 'd-none'}>
                            <Col lg="12" className="mb-4">
                                <Navigator
                                    pageTitle={item.title}
                                    translate={false}
                                    breadCrumbs={breadCrumbs}
                                />
                            </Col>
                        </Row>
                    ))}

                <Row className='g-3 mb-4 thirdparty-home-report-cards'>
                    {isAnonymously && (
                        <Col className={cardColClassName}>
                            <Card className='h-100 thirdparty-home-report-card'>
                                <CardBody className='report-card-body'>
                                    <div className='report-card-content'>
                                        <div className='report-card-text'>
                                            <h4 className='mb-2'>{t('Report anonymously')}</h4>
                                            <p className='font-size-14 mb-0'>
                                                {t(
                                                    'You do not disclose your identity. You communicate exclusively via the Smart Integrity Platform with those responsible from the company.',
                                                )}
                                            </p>
                                        </div>
                                        <div className='report-card-icon text-primary'>
                                            <img src={reportAIcon} alt='' />
                                        </div>
                                    </div>
                                </CardBody>
                                <CardBody className='border-top py-3'>
                                    <div className='text-truncate'>
                                        <Link to='/third-party/report-anonymously'>
                                            <Button color='btn btn-primary'>{t('Send a report')}</Button>
                                        </Link>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    )}
                    {isConfidentially && (
                        <Col className={cardColClassName}>
                            <Card className='h-100 thirdparty-home-report-card'>
                                <CardBody className='report-card-body'>
                                    <div className='report-card-content'>
                                        <div className='report-card-text'>
                                            <h4 className='mb-2'>
                                                {t(orgCustomization?.cm?.reportConfidentiallyTitle || 'Report confidentially')}
                                            </h4>
                                            <p className='font-size-14 mb-0'>
                                                {t(
                                                    orgCustomization?.cm?.reportConfidentiallyDesc ||
                                                    'You disclose your identity confidentially to the responsible persons and discuss your case discreetly with the responsible persons.',
                                                )}
                                            </p>
                                        </div>
                                        <div className='report-card-icon text-primary'>
                                            <img src={reportCIcon} alt='' />
                                        </div>
                                    </div>
                                </CardBody>
                                <CardBody className='border-top py-3'>
                                    <div className='text-truncate'>
                                        <Link to='/third-party/report-confidentially'>
                                            <Button color='btn btn-primary'>
                                                {t(orgCustomization?.cm?.reportConfidentiallyBtn || 'Send a report')}
                                            </Button>
                                        </Link>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    )}
                    {isMeeting && (
                        <Col className={cardColClassName}>
                            <Card className='h-100 thirdparty-home-report-card'>
                                <CardBody className='report-card-body'>
                                    <div className='report-card-content'>
                                        <div className='report-card-text'>
                                            <h4 className='mb-2'>{t('Arrange a meeting')}</h4>
                                            <p className='font-size-14 mb-0'>
                                                {t('You disclose your identity in order to arrange a confidential meeting with the responsible persons.')}
                                            </p>
                                        </div>
                                        <div className='report-card-icon text-primary'>
                                            <img src={arrangeMeetingIcon} alt='' />
                                        </div>
                                    </div>
                                </CardBody>
                                <CardBody className='border-top py-3'>
                                    <div className='text-truncate'>
                                        <Link to='arrange-meeting'>
                                            <Button color='btn btn-primary'>{t('Arrange a meeting')}</Button>
                                        </Link>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    )}
                    {isVoice && (
                        <Col className={cardColClassName}>
                            <Card className='h-100 thirdparty-home-report-card'>
                                <CardBody className='report-card-body'>
                                    <div className='report-card-content'>
                                        <div className='report-card-text'>
                                            <h4 className='mb-2'>{t('Voice report')}</h4>
                                            <p className='font-size-14 mb-0'>
                                                {t('Send a confidential or anonymous report with a voice message.')}
                                            </p>
                                        </div>
                                        <div className='report-card-icon text-primary'>
                                            <img src={voiceReportIcon} alt='' />
                                        </div>
                                    </div>
                                </CardBody>
                                <CardBody className='border-top py-3'>
                                    <div className='text-truncate'>
                                        <Link to='voice-report'>
                                            <Button color='btn btn-primary'>{t('Send a voice report')}</Button>
                                        </Link>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    )}
                </Row>

                {Organization.translations
                    .filter((tr) => tr.module === 'cm')
                    .map((item, index) => (
                        <Row key={index} className={item.lang === selectedLanguage ? '' : 'd-none'}>
                            <Col lg='12'>
                                <Card>
                                    <CardBody>
                                        <div className='introText' dangerouslySetInnerHTML={{ __html: item.intro }} />
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    ))}
            </Container>
        </div>
    );
};

const mapStatetoProps = (state) => {
	const { App, Organization, Modules : modules } = state;

	return {
		App,
		Organization,
		token: state.Login.token,
        modules,
        orgCustomization: selectOrgCustomization(state, Organization?.id)
	};
};

export default withNamespaces()(withRouter(connect(mapStatetoProps, {
})(CMHome)));
